import { useTranslation } from 'react-i18next';
import { useLabel } from '@platform/hooks/useLabel';
import { workspaceType, workspaceTypeOrder } from '@platform/navigation';
import type { IWorkspaceMenuProps } from './IWorkspaceMenuProps';
import { MenuOverlay } from './MenuOverlay';
import { workspaceGroup } from './workspaceGroup';
import { useMenuKeys } from './useMenuKeys';
import { WorkspaceMenuItem } from './WorkspaceMenuItem';

/**
 * Меню перемикача робочих просторів (прототип v4): 320px, групи за типом простору («Офіси депутатів», «Комітети»…) із заголовками 11px
 * великими літерами (парламент — у «Комітетах», після комітетів), пункти просторів; «Показати всі робочі простори…» — навігатор просторів;
 * «+ Створити робочий простір» — сторінка створення простору (прототип v4 newWs → onboard).
 */
export function WorkspaceMenu({ workspaces, current, onSelect, onClose, onBrowse, onCreate, trigger }: IWorkspaceMenuProps) {
  const { t } = useTranslation();
  const label = useLabel();
  const { menu, onKeyDown } = useMenuKeys<HTMLDivElement>(true, onClose, trigger);
  const groups = workspaceTypeOrder().map((type) => ({
    type, items: workspaces.filter((w) => workspaceGroup(w.type) === type).sort((a, b) => Number(a.type === 'parliament') - Number(b.type === 'parliament')),
  })).filter((g) => g.items.length);
  return (
    <>
      <MenuOverlay onClose={onClose} />
      <div ref={menu} role="menu" aria-label={t('shell.workspace')} className="tb-dd tb-wsmenu" onKeyDown={onKeyDown}>
        {groups.map((g) => (
          <div key={g.type} role="group" aria-label={label(workspaceType(g.type).plural)} className="tb-wsmenu__group">
            <div className="tb-dd__eyebrow">{label(workspaceType(g.type).plural)}</div>
            {g.items.map((w) => <WorkspaceMenuItem key={w.id} workspace={w} active={w.id === current} onSelect={onSelect} />)}
          </div>
        ))}
        <div className="tb-dd__sep" role="separator" />
        <button type="button" role="menuitem" className="tb-wsmenu__action" onClick={() => { onClose(); onBrowse(); }}>{t('shell.allWorkspaces')}</button>
        <button type="button" role="menuitem" className="tb-wsmenu__action is-accent" onClick={() => { onClose(); onCreate(); }}>{t('shell.createWorkspace')}</button>
      </div>
    </>
  );
}
